import type { GeometryKey } from '../locales/keys';

export type TransformMode = 'transform' | 'symmetry' | 'composition' | 'tessellation';
export type TransformPicker = 'translate' | 'rotate' | 'reflect' | 'dilate';
export type TilePicker = 'square' | 'hexagon' | 'triangle' | 'escher-square';

export interface TransformPreset {
  id: string;
  mode: TransformMode;
  labelKey: GeometryKey;
  advancedOnly: boolean;
  // transform mode
  picker?: TransformPicker;
  // rotate
  angleDeg?: number;
  // reflect: axis angle in degrees
  mirrorAngleDeg?: number;
  // translate: vector
  translateVec?: [number, number];
  // dilate
  dilateK?: number;
  // symmetry mode
  figureId?: string;
  // composition mode
  firstPicker?: TransformPicker;
  secondPicker?: TransformPicker;
  // tessellation mode
  tile?: TilePicker;
}

export const TRANSFORM_PRESETS: readonly TransformPreset[] = [
  {
    id: 'quarter-turn',
    mode: 'transform',
    labelKey: 'transform.preset.quarter-turn',
    advancedOnly: false,
    picker: 'rotate',
    angleDeg: 90,
  },
  {
    id: 'mirror-flip',
    mode: 'transform',
    labelKey: 'transform.preset.mirror-flip',
    advancedOnly: false,
    picker: 'reflect',
    mirrorAngleDeg: 90,
  },
  {
    id: 'parallel-mirrors',
    mode: 'composition',
    labelKey: 'transform.preset.parallel-mirrors',
    advancedOnly: true,
    firstPicker: 'reflect',
    secondPicker: 'reflect',
    mirrorAngleDeg: 90,
  },
  {
    id: 'crossing-mirrors',
    mode: 'composition',
    labelKey: 'transform.preset.crossing-mirrors',
    advancedOnly: true,
    firstPicker: 'reflect',
    secondPicker: 'reflect',
    mirrorAngleDeg: 45,
  },
  {
    id: 'hexagon-tiling',
    mode: 'tessellation',
    labelKey: 'transform.preset.hexagon-tiling',
    advancedOnly: true,
    tile: 'hexagon',
  },
];

export function isValidTransformPreset(p: TransformPreset): boolean {
  if (typeof p.id !== 'string' || p.id.length === 0) return false;
  const validModes: TransformMode[] = ['transform', 'symmetry', 'composition', 'tessellation'];
  if (!validModes.includes(p.mode)) return false;
  if (typeof p.labelKey !== 'string' || p.labelKey.length === 0) return false;
  if (typeof p.advancedOnly !== 'boolean') return false;
  const validPickers: TransformPicker[] = ['translate', 'rotate', 'reflect', 'dilate'];
  if (p.picker !== undefined && !validPickers.includes(p.picker)) return false;
  if (p.firstPicker !== undefined && !validPickers.includes(p.firstPicker)) return false;
  if (p.secondPicker !== undefined && !validPickers.includes(p.secondPicker)) return false;
  const validTiles: TilePicker[] = ['square', 'hexagon', 'triangle', 'escher-square'];
  if (p.tile !== undefined && !validTiles.includes(p.tile)) return false;
  if (p.angleDeg !== undefined && typeof p.angleDeg !== 'number') return false;
  if (p.mirrorAngleDeg !== undefined && typeof p.mirrorAngleDeg !== 'number') return false;
  if (p.dilateK !== undefined && typeof p.dilateK !== 'number') return false;
  if (p.translateVec !== undefined) {
    if (!Array.isArray(p.translateVec) || p.translateVec.length !== 2) return false;
    if (typeof p.translateVec[0] !== 'number' || typeof p.translateVec[1] !== 'number')
      return false;
  }
  return true;
}

export function validateTransformPresetIds(presets: readonly TransformPreset[]): boolean {
  const ids = new Set<string>();
  for (const preset of presets) {
    if (ids.has(preset.id)) return false;
    ids.add(preset.id);
  }
  return true;
}
